import { useCallback, useEffect, useState } from 'react';
import { api, clearToken, getToken, setToken } from '../api/client.js';

/**
 * Récupère le token depuis le fragment `#token=...` posé par le callback
 * OAuth backend, le stocke en localStorage et nettoie l'URL.
 * Retourne true si un token a été extrait du fragment.
 */
function consumeTokenFromHash() {
  const hash = window.location.hash;
  if (!hash || !hash.startsWith('#')) return false;
  const params = new URLSearchParams(hash.slice(1));
  const token = params.get('token');
  if (!token) return false;
  setToken(token);
  // Nettoie le hash de l'URL sans recharger
  window.history.replaceState(null, '', window.location.pathname + window.location.search);
  return true;
}

/**
 * Récupère un éventuel `auth_error` posé par le backend en cas de
 * domaine refusé ou erreur OAuth. Retourne le message d'erreur et
 * nettoie l'URL.
 */
function consumeAuthErrorFromQuery() {
  const params = new URLSearchParams(window.location.search);
  const err = params.get('auth_error');
  if (!err) return null;
  params.delete('auth_error');
  const newSearch = params.toString();
  window.history.replaceState(
    null,
    '',
    window.location.pathname + (newSearch ? `?${newSearch}` : ''),
  );
  return err;
}

export function useAuth() {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [authError, setAuthError] = useState(null);

  // Au mount : consomme le token du fragment, puis charge le profil
  useEffect(() => {
    const errFromQuery = consumeAuthErrorFromQuery();
    if (errFromQuery) setAuthError(errFromQuery);

    consumeTokenFromHash();

    if (!getToken()) {
      setLoading(false);
      return;
    }

    api.auth
      .me()
      .then(({ user }) => setUser(user))
      .catch(() => {
        clearToken();
        setUser(null);
      })
      .finally(() => setLoading(false));
  }, []);

  const login = useCallback((next = '/') => {
    window.location.href = api.auth.googleLoginUrl(next);
  }, []);

  const logout = useCallback(async () => {
    try {
      await api.auth.logout();
    } catch {
      // Le token peut déjà être invalide — on continue
    }
    clearToken();
    setUser(null);
  }, []);

  return { user, loading, authError, login, logout, isAuthenticated: Boolean(user) };
}
